/// <amd-module name="Build/Admin/Common/Components/VMScaleSetSettings" />
import * as React from "react";

import { ObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";

import { AzureSubscription, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { ServiceEndpointActionCreator } from "Build/Admin/Common/Actions/ServiceEndpointActionCreator";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { StoreStatus, ConfigurationJob } from "Build/Admin/Common/Constants";
import { ServiceEndpointStore } from "Build/Admin/Common/Stores/ServiceEndpointStore";
import { VirtualMachineScaleSetStore } from "Build/Admin/Common/Stores/VirtualMachineScaleSetStore";

import * as Resources from "Build/Admin/Common/Resources";

const VmssTagKey: string = "__AzureDevOpsElasticPool";
const VmssTimeStampKey: string = "__AzureDevOpsElasticPoolTimeStamp";

export interface IVMScaleSetSettingsProps {
    actionCreator: ServiceEndpointActionCreator;
    serviceEndpointStore: ServiceEndpointStore;
    virtualMachineScaleSetStore: VirtualMachineScaleSetStore;
    projectId: string | undefined;
    onSettingsChanged(serviceEndpoint: ServiceEndpoint | undefined, vmScaleSet: VirtualMachineScaleSet | undefined): void;
}

interface IVMScaleSetSettingsState {
    authorizing: boolean;
    needAuthorizing: boolean;
    authorizingEndpointName: string;
    azureSubscriptionsLoading: boolean;
    serviceEndpointsLoading: boolean;
    vmssLoading: boolean;
    vmssErrorMessage: string | undefined;
}

const connectionsGroupKey = "connections";
const subscriptionsGroupKey = "subscriptions";

export class VMScaleSetSettings extends VssComponent<IVMScaleSetSettingsProps, IVMScaleSetSettingsState> {
    private _subscriptionItemProvider = new GroupedItemProvider<AzureSubscription | ServiceEndpoint>([],[
        {id: connectionsGroupKey, loading: true, name: Resources.AzureConnectionGroupTitle},
        {id: subscriptionsGroupKey, loading: true, name: Resources.AzureSubscriptionGroupTitle}
    ], true);
    private _subscriptionSelection = new DropdownSelection();
    private _vmssItemProvider = new ObservableArray<IListBoxItem<VirtualMachineScaleSet>>();

    constructor(props: IVMScaleSetSettingsProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            authorizing: false,
            needAuthorizing: false,
            authorizingEndpointName: "",
            azureSubscriptionsLoading: props.serviceEndpointStore.state.azureSubscriptionsStatus === StoreStatus.Loading,
            serviceEndpointsLoading: props.serviceEndpointStore.state.serviceEndpointsStatus === StoreStatus.Loading,
            vmssLoading: props.virtualMachineScaleSetStore.state.virtualMachineScaleSetsStatus === StoreStatus.Loading,
            vmssErrorMessage: undefined
        };

        this._updateConnectionOptions(false);
    }

    public componentDidMount(): void {
        this.props.serviceEndpointStore.addChangedListener(this._updateConnectionOptions);
        this.props.virtualMachineScaleSetStore.addChangedListener(this._updateVirtualMachineScaleSetOptions);

        this.props.actionCreator.loadAzureSubscriptions();
        if (this.props.projectId) {
            this.props.actionCreator.loadServiceEndpoints(this.props.projectId);
        }
    }

    public componentWillUnmount(): void {
        this.props.serviceEndpointStore.removeChangedListener(this._updateConnectionOptions);
        this.props.virtualMachineScaleSetStore.removeChangedListener(this._updateVirtualMachineScaleSetOptions);
    }

    public componentDidUpdate(prevProps: IVMScaleSetSettingsProps): void {
        if (this.props.projectId !== prevProps.projectId) {
            this._vmssItemProvider.removeAll();
            if (this.props.projectId !== undefined) {
                this.props.actionCreator.loadServiceEndpoints(this.props.projectId);
            }
        }
    }

    public render(): JSX.Element {
        const showAuthorizeButton: boolean = this.state.needAuthorizing;
        const authorizeButtonLabel: string = this.state.authorizing ? Resources.Authorizing : Resources.Authorize;
        const showAuthorizeSpinner: boolean = this.state.authorizing;
        const disableVMSSDropdown: boolean = this._vmssItemProvider.length === 0 && !this.state.vmssLoading;

        return (
            <div className="vm-scale-set-settings">
                <div className="pool-setting-label">
                    {Resources.AzureSubscriptionLabel}
                </div>
                <div className="flex-row">
                    <Dropdown
                        className="azure-subscription-dropdown flex-grow"
                        disabled={this._subscriptionItemProvider.length === 0 || this.props.projectId === undefined}
                        placeholder={Resources.SelectAzureSubscriptionLabel}
                        items={this._subscriptionItemProvider}
                        selection={this._subscriptionSelection}
                        onSelect={this._onConnectionOptionSelectionChanged} />
                    <Button
                        ariaLabel={Resources.ReloadAzureSubscriptionsAriaLabel}
                        className="reload-button"
                        disabled={this.state.azureSubscriptionsLoading || this.state.serviceEndpointsLoading || !this.props.projectId || this.state.authorizing}
                        iconProps={{iconName: "Refresh"}}
                        onClick={this._reloadAzureSubscriptions} />
                </div>
                <ConditionalChildren renderChildren={showAuthorizeButton}>
                    <div className="font-size secondary-text margin-top-8">{Resources.SubscriptionNeedsAuthorization}</div>
                    <Button className="authorize-button flex-self-start"
                            onClick={this._onAuthorize}
                            primary={true}
                            disabled={this.state.authorizing}>
                        {showAuthorizeSpinner && <Spinner size={SpinnerSize.small} />}
                        {authorizeButtonLabel}
                    </Button>
                </ConditionalChildren>
                <div className="pool-setting-label">
                    {Resources.VirtualMachineScaleSetLabel}
                </div>
                <div className="flex-row">
                    <Dropdown
                        className={css("virtual-machine-scale-set-dropdown flex-grow", (this.state.vmssErrorMessage !== undefined) ? "bolt-textfield-error" : undefined)}
                        disabled={disableVMSSDropdown}
                        placeholder={Resources.SelectVirtualMachineScaleSetLabel}
                        items={this._vmssItemProvider}
                        onSelect={this._onVirtualMachineScaleSetSelected}
                        loading={this.state.vmssLoading} />
                    <Button
                        ariaLabel={Resources.ReloadVirtualMachineScaleSetsAriaLabel}
                        className="reload-button"
                        disabled={this.state.azureSubscriptionsLoading || this.state.serviceEndpointsLoading || this.state.vmssLoading || !this.props.projectId || !this._subscriptionSelection.selectedCount}
                        iconProps={{iconName: "Refresh"}}
                        onClick={this._reloadVirtualMachineScaleSets} />
                </div>
                <ConditionalChildren renderChildren={(this.state.vmssErrorMessage !== undefined)}>
                    <div className="bolt-formitem-message bolt-formitem-message-error body-xs">{this.state.vmssErrorMessage}</div>
                </ConditionalChildren>
            </div>
        );
    }

    private _onConnectionOptionSelectionChanged = (event: React.SyntheticEvent<HTMLElement> | undefined, item: IListBoxItem<AzureSubscription | ServiceEndpoint>): void => {
        if (item.groupId === subscriptionsGroupKey) {
            this.props.onSettingsChanged(undefined, undefined);
            this._vmssItemProvider.removeAll();
            this.setState({
                authorizing: false,
                needAuthorizing: true,
                authorizingEndpointName: "",
                vmssErrorMessage: undefined
            });
        }
        else if (item.groupId === connectionsGroupKey) {
            const serviceEndpoint: ServiceEndpoint = item.data as ServiceEndpoint;
            this.props.onSettingsChanged(serviceEndpoint, undefined);
            this._vmssItemProvider.removeAll();
            this.setState({
                authorizing: false,
                needAuthorizing: false,
                authorizingEndpointName: "",
                vmssErrorMessage: undefined
            });

            if (event !== undefined && this.props.projectId) {
                this.props.actionCreator.loadVirtualMachineScaleSets(this.props.projectId, serviceEndpoint);
            }
        }
    }

    private _reloadAzureSubscriptions = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        this.props.actionCreator.loadAzureSubscriptions(true);
        if (this.props.projectId !== undefined) {
            this._vmssItemProvider.removeAll();
            this.props.actionCreator.loadServiceEndpoints(this.props.projectId, true);
            this.props.onSettingsChanged(undefined, undefined);
        }
    }

    private _onVirtualMachineScaleSetSelected = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<VirtualMachineScaleSet>): void => {
        if (!this._subscriptionSelection.selectedCount) {
            return;
        }

        const subscriptionItem: IListBoxItem<AzureSubscription | ServiceEndpoint> = this._subscriptionItemProvider.value[this._subscriptionSelection.value[0].beginIndex];
        if (subscriptionItem.groupId === connectionsGroupKey) {
            // Notify the user if the VMSS is tagged as already in use by another pool
            const isTimeStampTagValid = this._isVmssTimeStampTagValid(item.data?.tags?.[VmssTimeStampKey]);
            if (item.data?.tags?.[VmssTagKey] !== undefined && isTimeStampTagValid)   {
                this.setState({
                    vmssErrorMessage: format(Resources.VirtualMachineScaleSetInUseError, item.data.tags[VmssTagKey])
                });
                this.props.onSettingsChanged(subscriptionItem.data as ServiceEndpoint, undefined);
            }
            else {
                this.setState({
                    vmssErrorMessage: this.props.virtualMachineScaleSetStore.state.virtualMachineScaleSetsErrorMessage
                });
                this.props.onSettingsChanged(subscriptionItem.data as ServiceEndpoint, item.data);
            }
        }
    }

    private _isVmssTimeStampTagValid = (text: string | undefined): boolean => {
        if (text == undefined)
            return true;

        const timestamp = new Date(text);
        const utcNow = this._shiftToUTC(new Date());
        const diffTime = utcNow.getTime() - timestamp.getTime(); //miliseconds
        const diffHours = Math.trunc(diffTime / (1000 * 60 * 60)); //hours

        if (diffHours >= ConfigurationJob.VmssTimestampValidityHours)
            return false;

        return  true;
    }

    private _shiftToUTC = (date: Date): Date => {
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
    }

    private _reloadVirtualMachineScaleSets = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        if (this.props.projectId !== undefined && this._subscriptionSelection.selectedCount > 0) {
            const subscriptionItem: IListBoxItem<AzureSubscription | ServiceEndpoint> = this._subscriptionItemProvider.value[this._subscriptionSelection.value[0].beginIndex];
            if (subscriptionItem.groupId === connectionsGroupKey) {
                const serviceEndpoint: ServiceEndpoint = subscriptionItem.data as ServiceEndpoint;
                this.props.actionCreator.loadVirtualMachineScaleSets(this.props.projectId, serviceEndpoint);
                this.props.onSettingsChanged(serviceEndpoint, undefined);
            }
        }
    }

    private _updateVirtualMachineScaleSetOptions = (): void => {
        this._vmssItemProvider.removeAll();

        const vmssErrorMessage: string | undefined = this.props.virtualMachineScaleSetStore.state.virtualMachineScaleSetsErrorMessage;
        const vmssLoading: boolean = this.props.virtualMachineScaleSetStore.state.virtualMachineScaleSetsStatus === StoreStatus.Loading;
        if (vmssLoading) {
            this._vmssItemProvider.push(
                { id: "loading", type: ListBoxItemType.Loading } as IListBoxItem<VirtualMachineScaleSet>
            );
        }
        else {
            const virtualMachineScaleSets: IListBoxItem<VirtualMachineScaleSet>[] =
                this.props.virtualMachineScaleSetStore.state.virtualMachineScaleSets.map(vmss => {
                    return {
                        id: vmss.id,
                        text: vmss.name,
                        data: vmss
                    } as IListBoxItem<VirtualMachineScaleSet>;
            });
            this._vmssItemProvider.push(...virtualMachineScaleSets);
        }

        this.setState({
            vmssLoading,
            vmssErrorMessage
        });
    }

    private _updateConnectionOptions = (setState: boolean = true): void => {
        // Store selection if there is one, so we can reselect after an update
        let prevSelectedItemId: string | undefined = undefined;
        if (this._subscriptionSelection.selectedCount > 0) {
            prevSelectedItemId = this._subscriptionItemProvider.value[this._subscriptionSelection.value[0].beginIndex].id;
        }

        this._subscriptionItemProvider.removeAll();

        if (this.props.serviceEndpointStore.state.serviceEndpointsStatus === StoreStatus.Loaded &&
            this.props.serviceEndpointStore.state.serviceEndpointScope === this.props.projectId) {
            const serviceEndpoints: IListBoxItem<ServiceEndpoint>[] =
                this.props.serviceEndpointStore.state.serviceEndpoints.map(connection => {
                    return {
                        id: connection.id,
                        text: connection.name,
                        groupId: connectionsGroupKey,
                        data: connection
                    } as IListBoxItem<ServiceEndpoint>;
            });

            serviceEndpoints.sort((a, b) => {
                return a.text!.localeCompare(b.text!, navigator.language);
            });

            if (serviceEndpoints) {
                this._subscriptionItemProvider.pushGroups({
                    id: connectionsGroupKey,
                    name: Resources.AzureConnectionGroupTitle
                });
                this._subscriptionItemProvider.push(...serviceEndpoints);
            }
        }
        else {
            this._subscriptionItemProvider.pushGroups({
                id: connectionsGroupKey,
                name: Resources.AzureConnectionGroupTitle,
                loading: true
            });
        }

        if (this.props.serviceEndpointStore.state.azureSubscriptionsStatus === StoreStatus.Loaded) {
            const azureSubscriptions: IListBoxItem<AzureSubscription>[] =
                this.props.serviceEndpointStore.state.azureSubscriptions.map(subscription => {
                    return {
                        id: subscription.subscriptionId,
                        text: this.props.actionCreator.getSubscriptionDisplayName(subscription),
                        groupId: subscriptionsGroupKey,
                        data: subscription
                    } as IListBoxItem<AzureSubscription>;
            });

            azureSubscriptions.sort((a, b) => {
                return a.text!.localeCompare(b.text!, navigator.language);
            });

            if (azureSubscriptions) {
                this._subscriptionItemProvider.pushGroups({
                    id: subscriptionsGroupKey,
                    name: Resources.AzureSubscriptionGroupTitle
                });
                this._subscriptionItemProvider.push(...azureSubscriptions);
            }
        }
        else {
            this._subscriptionItemProvider.pushGroups({
                id: subscriptionsGroupKey,
                name: Resources.AzureSubscriptionGroupTitle,
                loading: true
            });
        }

        // Restore the selection from before the update
        if (prevSelectedItemId !== undefined) {
            const previouslySelectedIndex: number = this._subscriptionItemProvider.value.findIndex(i => i.id === prevSelectedItemId);
            if (previouslySelectedIndex !== -1) {
                this._subscriptionSelection.select(previouslySelectedIndex);
            }
        }

        // An update will occur if a new endpoint was authorized. Check to see if we are waiting on endpoint
        // authorization, if so, see if that new endpoint was added to the list and select it
        if (this.state.authorizingEndpointName) {
            const authorizedEndpointIndex: number = this._subscriptionItemProvider.value.findIndex(i => i.text === this.state.authorizingEndpointName);
            if (authorizedEndpointIndex !== -1) {
                this._subscriptionSelection.select(authorizedEndpointIndex);
                this._onConnectionOptionSelectionChanged(undefined, this._subscriptionItemProvider.value[authorizedEndpointIndex]);
            }
        }

        if (setState) {
            const azureSubscriptionsLoading: boolean = this.props.serviceEndpointStore.state.azureSubscriptionsStatus === StoreStatus.Loading;
            const serviceEndpointsLoading: boolean = this.props.serviceEndpointStore.state.serviceEndpointsStatus === StoreStatus.Loading;
            this.setState({
                azureSubscriptionsLoading,
                serviceEndpointsLoading
            });
        }
    }

    private _onAuthorize = (): void => {
        if (!this._subscriptionSelection.selectedCount) {
            return;
        }

        const subscriptionItem: IListBoxItem<AzureSubscription | ServiceEndpoint> = this._subscriptionItemProvider.value[this._subscriptionSelection.value[0].beginIndex];

        if (subscriptionItem.groupId !== subscriptionsGroupKey || !subscriptionItem.data) {
            return;
        }

        const subscription = subscriptionItem.data as AzureSubscription;

        if (this.props.projectId) {
            const authorizingEndpointName: string = this.props.actionCreator.authorizeNewServiceEndpoint(this.props.projectId, subscription);

            this.setState({
                authorizing: true,
                authorizingEndpointName
            });
        }
    }
}
